import { useEffect, useMemo, useState } from "react";
import { money } from "@/lib/human";
import { useLocation, useNavigate } from "react-router-dom";
import { Coins, ScanLine, Target, ShieldCheck, ArrowRight, Loader2, Check, Lock, Landmark, MousePointerClick } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { StaggerGroup, StaggerItem } from "@/components/motion/Stagger";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { trackCheckoutStarted } from "@/lib/tracking";

const DEFAULT_PLAN = "pro_special";

// Format a Date as a readable Spanish date, e.g. "20 de junio de 2026".
function formatDateEs(d) {
  try {
    return new Intl.DateTimeFormat("es", { day: "numeric", month: "long", year: "numeric" }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Post-registration value screen. Shown BEFORE the hosted Stripe form (which we
 * cannot restyle). Presents the product, three concrete value bullets, and the
 * EXACT rebill date + amount computed from the real package (anti-chargeback:
 * never a placeholder), then starts the $1 checkout.
 */
export default function ActivatePage() {
  const { user, refresh } = useAuth();
  const reduceMotion = useReducedMotion();
  const nav = useNavigate();
  const loc = useLocation();
  const planId = new URLSearchParams(loc.search).get("plan") || DEFAULT_PLAN;

  // The chosen tier (pro/family) is fixed by whatever plan brought the user here;
  // the on-screen selector only switches billing cadence within that tier, plus
  // the tier-agnostic Lifetime option. Lifetime uses the existing lifetime_offer
  // SKU (see backend/billing.py) — no new SKU is created here.
  const tier = planId.startsWith("family") ? "family" : "pro";
  const OPTIONS = [
    { key: `${tier}_special`, label: "Mensual" },
    { key: `${tier}_yearly`, label: "Anual" },
    { key: "lifetime_offer", label: "De por vida" },
  ];
  const initialKey = (() => {
    if (planId === "lifetime_offer" || planId === "lifetime_regular") return "lifetime_offer";
    if (planId.endsWith("_yearly")) return `${tier}_yearly`;
    return `${tier}_special`;
  })();

  const [selectedKey, setSelectedKey] = useState(initialKey);
  const [packages, setPackages] = useState(null);
  const [dopRate, setDopRate] = useState(null); // live USDT/DOP median (≈ USD/DOP), referential only
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinErr, setJoinErr] = useState("");

  useEffect(() => {
    api.get("/billing/packages")
      .then((r) => setPackages(r.data))
      .catch(() => setPackages({}));
  }, []);

  // Live USDT/DOP median from Binance P2P (anon, same source as the landing).
  // USDT ≈ USD, so this is a good *referential* peso figure for a Dominican buyer.
  // Soft-fail: if it doesn't load we simply omit the peso line (never block/guess).
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fx/binance-p2p?asset=USDT&fiat=DOP&trade_type=SELL`,
      { headers: { "X-Anon": "1" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.price && setDopRate(Number(d.price)))
      .catch(() => {});
  }, []);

  // Resolve the chosen package, falling back to the default $1 hook if the
  // query param is unknown/missing.
  const pkg = useMemo(() => {
    if (!packages) return null;
    return packages[selectedKey] || packages[DEFAULT_PLAN] || null;
  }, [packages, selectedKey]);

  // Short price label for each selector pill, derived from the real package.
  const optionPriceLabel = (key) => {
    const p = packages?.[key];
    if (!p) return "";
    if (p.trial) return "$1 · 7 días";
    if (p.mode === "payment") return `${money(p.amount, p.currency)} único`;
    return `${money(p.amount, p.currency)}/${p.interval === "year" ? "año" : "mes"}`;
  };

  // Exact billing summary derived from the real server-defined package.
  const billing = useMemo(() => {
    if (!pkg) return null;
    const currency = pkg.currency || "usd";
    if (pkg.trial) {
      // Offer model (see backend/billing.py): $1 today, 7-day access, then the
      // first recurring invoice on day 7 = full price minus the $1 welcome
      // coupon, and normal price on every renewal after that.
      const rebillDate = new Date();
      rebillDate.setDate(rebillDate.getDate() + 7);
      const firstCharge = Math.max(0, Number(pkg.amount) - 1);
      return {
        todayLabel: `Hoy pagas ${money(1, currency)}`,
        rebillLine: `El ${formatDateEs(rebillDate)} se cobra ${money(firstCharge, currency)}, salvo que canceles antes desde tu perfil.`,
        note: firstCharge !== Number(pkg.amount)
          ? `Luego ${money(pkg.amount, currency)} / ${pkg.interval === "year" ? "año" : "mes"}, cada mes.`
          : "",
        cta: `Empezar por ${money(1, currency)}`,
      };
    }
    if (pkg.mode === "payment") {
      return {
        todayLabel: `Pago único de ${money(pkg.amount, currency)}`,
        rebillLine: "Acceso de por vida. Sin renovaciones.",
        note: "",
        cta: `Obtener acceso · ${money(pkg.amount, currency)}`,
      };
    }
    const per = pkg.interval === "year" ? "año" : "mes";
    return {
      todayLabel: `Hoy pagas ${money(pkg.amount, currency)}`,
      rebillLine: `Se renueva cada ${per} a ${money(pkg.amount, currency)}. Cancela cuando quieras.`,
      note: "",
      cta: `Activar · ${money(pkg.amount, currency)}`,
    };
  }, [pkg]);

  // Invited-member path: redeeming a household invite grants inherited access
  // (the owner already paid) without a separate purchase. This is why the join
  // endpoint is exempt from the server-side gate.
  const joinHousehold = async () => {
    const code = joinCode.trim();
    if (!code) return;
    setJoinErr("");
    setJoining(true);
    try {
      await api.post("/household/join", { code });
      await refresh(); // /auth/me now returns has_access=true (inherited)
      nav("/", { replace: true });
    } catch (e) {
      setJoinErr(e?.response?.data?.detail || "No se pudo unir al hogar. Verifica el código.");
    } finally {
      setJoining(false);
    }
  };

  const startCheckout = async () => {
    if (!pkg) return;
    setErr("");
    setLoading(true);
    try {
      const r = await api.post("/billing/checkout", {
        package_id: pkg.id || selectedKey,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        // Funnel event 5 — real Stripe Checkout Session was created for this
        // (session, plan). This is the main post-registration checkout entry
        // point in the binary-paywall flow.
        trackCheckoutStarted(pkg.id || selectedKey, r.data.session_id || selectedKey, 1);
        window.location.href = r.data.url;
      } else {
        setErr("No se pudo iniciar el pago. Intenta de nuevo.");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo iniciar el pago. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const bullets = [
    { icon: Coins, title: "Multi-moneda en vivo", desc: "DOP, USD, EUR, USDT y más — tasas y conversión al instante." },
    { icon: ScanLine, title: "OCR de recibos", desc: "Fotografía un recibo y se registra la transacción en segundos." },
    { icon: Target, title: "Metas y presupuestos", desc: "Define objetivos, controla gastos y mira tu progreso mes a mes." },
  ];

  const planName = pkg?.name || "";

  // Referential peso figure for the recurring/one-time price of the chosen plan.
  // For the trial, this is the ongoing monthly price (what matters after day 7),
  // not the $1 hook. Only rendered when the live rate is available.
  const pesoLine = (() => {
    if (!pkg || !dopRate) return null;
    const baseUsd = Number(pkg.amount || 0);
    if (baseUsd <= 0) return null;
    const cadence = pkg.trial ? "/mes"
      : pkg.mode === "payment" ? " pago único"
      : pkg.interval === "year" ? "/año" : "/mes";
    const dop = Math.round(baseUsd * dopRate).toLocaleString("es-DO");
    return `≈ RD$ ${dop}${cadence}`;
  })();

  return (
    <div className="min-h-screen bg-[#0b0a10] text-white">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 lg:py-16">
        {/* Header */}
        <div className="mb-9">
          <div className="text-[10px] uppercase tracking-[0.28em] text-violet-300 font-bold" data-testid="activate-eyebrow">
            Casi listo{user?.name ? `, ${user.name}` : ""}
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight leading-[1.05]" data-testid="activate-title">
            Activa tu acceso completo.
          </h1>
          <p className="mt-3 text-sm text-slate-400 max-w-xl">
            Todas las herramientas de The Trading Dock, sin límites. Empieza hoy por {money(1)} y decide con calma.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* LEFT — product preview + bullets */}
          <div>
            {/* Static product preview (faux dashboard) */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]" data-testid="activate-preview" aria-hidden="true">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Patrimonio neto</div>
                  <div className="text-2xl font-black tabular-nums text-white mt-1">$12,480.50</div>
                </div>
                <div className="text-[11px] px-2 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 font-semibold">+8.2%</div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[["DOP", "RD$ 310k"], ["USD", "$4,120"], ["USDT", "1,890"]].map(([c, v]) => (
                  <div key={c} className="rounded-xl bg-white/[0.04] border border-white/5 px-3 py-2.5">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{c}</div>
                    <div className="text-sm font-bold text-white mt-0.5 tabular-nums">{v}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-1.5 h-20">
                {[40, 65, 45, 80, 55, 95].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-md bg-violet-500/50" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>

            <StaggerGroup as="ul" className="mt-6 space-y-3" data-testid="activate-bullets">
              {bullets.map((b) => (
                <StaggerItem as="li" key={b.title} className="flex items-start gap-3">
                  <div className="mt-0.5 w-9 h-9 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
                    <b.icon className="w-4.5 h-4.5 text-violet-300" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{b.title}</div>
                    <div className="text-[13px] text-slate-400 leading-snug">{b.desc}</div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>

          {/* RIGHT — offer card + CTA */}
          <div className="rounded-3xl border border-white/10 bg-[#121019] p-6 sm:p-7" data-testid="activate-offer-card">
            {planName && (
              <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 font-bold mb-1">{planName}</div>
            )}

            {/* Plan selector — mensual / anual / de por vida (Lifetime). */}
            {packages && (
              <div className="grid grid-cols-3 gap-2 mb-5" data-testid="activate-plan-selector">
                {OPTIONS.map((o) => {
                  const active = selectedKey === o.key;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setSelectedKey(o.key)}
                      aria-pressed={active}
                      data-testid={`activate-option-${o.key}`}
                      className={`rounded-xl border px-2 py-2.5 text-center transition ${
                        active
                          ? "border-violet-500 bg-violet-500/15"
                          : "border-white/10 bg-white/[0.03] hover:border-white/20"
                      }`}
                    >
                      <div className="text-[12px] font-bold text-white leading-tight">{o.label}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5 tabular-nums">{optionPriceLabel(o.key)}</div>
                    </button>
                  );
                })}
              </div>
            )}

            {!billing ? (
              <div className="py-10 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
              </div>
            ) : (
              <>
                <div className="text-3xl font-black tracking-tight text-white" data-testid="activate-today">
                  {billing.todayLabel}
                </div>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed" data-testid="activate-rebill">
                  {billing.rebillLine}
                </p>
                {billing.note && (
                  <p className="mt-1 text-[12px] text-slate-500" data-testid="activate-note">{billing.note}</p>
                )}

                {/* Peso equivalent + international-charge advisory — high value for the
                    Dominican ICP. The peso figure is referential (live USDT/DOP); the
                    charge itself is in USD and any FX/intl fee is set by the buyer's bank. */}
                <div className="mt-4 rounded-xl bg-white/[0.03] border border-white/10 px-3.5 py-3" data-testid="activate-dop-advisory">
                  {pesoLine && (
                    <div className="flex items-center gap-2 text-[13px] text-slate-200">
                      <Landmark className="w-3.5 h-3.5 text-violet-300 shrink-0" />
                      <span>En pesos: <strong className="text-white tabular-nums">{pesoLine}</strong></span>
                      <span className="text-[11px] text-slate-500">tasa referencial</span>
                    </div>
                  )}
                  <p className={`text-[11px] text-slate-500 leading-relaxed ${pesoLine ? "mt-1.5" : ""}`}>
                    El cobro se hace en USD. Tu banco podría aplicar un cargo por transacción internacional; eso lo decide tu banco, no nosotros.
                  </p>
                </div>

                <div className="my-5 h-px bg-white/10" />

                <ul className="space-y-2 mb-6">
                  {["Acceso completo desde hoy", "Sin permanencia, cancela en un clic", "Pago seguro con Stripe"].map((t) => (
                    <li key={t} className="flex items-center gap-2.5 text-[13px] text-slate-300">
                      <Check className="w-4 h-4 text-violet-400 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>

                {err && (
                  <div className="mb-4 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="activate-error">
                    {err}
                  </div>
                )}

                <motion.button
                  onClick={startCheckout}
                  disabled={loading}
                  data-testid="activate-cta"
                  className="w-full flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-400 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60"
                  {...(reduceMotion || loading ? {} : { whileHover: { scale: 1.02 }, whileTap: { scale: 0.97 }, transition: { duration: 0.14, ease: [0.23, 1, 0.32, 1] } })}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{billing.cta} <ArrowRight className="w-4 h-4" /></>}
                </motion.button>

                {/* Verifiable trust markers (real facts reused from the FAQ) — no
                    invented user counts or ratings. */}
                <div className="mt-4 grid grid-cols-2 gap-2" data-testid="activate-trust-markers">
                  {[
                    { icon: ShieldCheck, label: "Pagos vía Stripe" },
                    { icon: Lock, label: "Cifrado, no guardamos tu tarjeta" },
                    { icon: Landmark, label: "Sin acceso a tu banco" },
                    { icon: MousePointerClick, label: "Cancela en 1 clic" },
                  ].map((tm) => (
                    <div key={tm.label} className="flex items-center gap-2 rounded-lg bg-white/[0.03] border border-white/10 px-2.5 py-2 text-[11px] text-slate-300">
                      <tm.icon className="w-3.5 h-3.5 text-violet-300 shrink-0" />
                      <span className="leading-tight">{tm.label}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => nav("/perfil")}
                  data-testid="activate-manage-link"
                  className="mt-4 w-full text-center text-[12px] text-slate-500 hover:text-slate-300"
                >
                  Ver otros planes
                </button>

                {/* Invited-member entry point */}
                <div className="mt-5 pt-4 border-t border-white/10">
                  {!showJoin ? (
                    <button
                      onClick={() => setShowJoin(true)}
                      data-testid="activate-join-toggle"
                      className="w-full text-center text-[12px] text-violet-300 hover:text-violet-200 font-semibold"
                    >
                      ¿Te invitaron a un hogar? Únete con tu código
                    </button>
                  ) : (
                    <div data-testid="activate-join-box">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-1.5">Código de invitación</div>
                      <div className="flex gap-2">
                        <input
                          value={joinCode}
                          onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                          placeholder="EJ: 7GX2Q9AB"
                          data-testid="activate-join-input"
                          className="flex-1 bg-white/[0.04] border border-white/10 focus:border-violet-500 rounded-xl px-3 py-2.5 text-sm text-white outline-none uppercase tracking-wide"
                        />
                        <button
                          onClick={joinHousehold}
                          disabled={joining || !joinCode.trim()}
                          data-testid="activate-join-submit"
                          className="px-4 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold disabled:opacity-50"
                        >
                          {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unirme"}
                        </button>
                      </div>
                      {joinErr && <div className="mt-2 text-[12px] text-rose-300" data-testid="activate-join-error">{joinErr}</div>}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
